import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, Building2, GraduationCap, Loader2, RefreshCw, ArrowLeftRight, ListChecks, Map, ChevronLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardHeader } from "@/components/DashboardHeader";
import AccessDenied from "@/components/AccessDenied";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ikon pr. liste-id
const LISTE_ICON = {
  kunder:     <Users className="w-6 h-6" />,
  b2b_kunder: <Building2 className="w-6 h-6" />,
  elever:     <GraduationCap className="w-6 h-6" />,
};

// Lille tal-kort til synk-statistikken.
function StatKort({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/40 px-3 py-3 sm:px-4">
      <div className={`text-xl font-bold tabular-nums ${accent || "text-foreground"}`}>
        {(value ?? 0).toLocaleString("da-DK")}
      </div>
      <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{label}</div>
    </div>
  );
}

// Synk-status: viser hvor mange synkroniseringer connectoren har kørt.
function SynkStatus({ stats, loading, onRefresh }) {
  const g = (k) => Number(stats?.[k] || 0);
  // Til/fra-meldinger fordelt på begge systemer.
  const tilmeldt = g("heyloyalty:subscribed") + g("gecko:subscribed");
  const afmeldt = g("heyloyalty:unsubscribed") + g("gecko:unsubscribed");
  const harData = stats && Object.keys(stats).some((k) => k !== "_recent");

  return (
    <div className="mt-8 rounded-2xl border border-border bg-card p-4 sm:p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5 min-w-0">
          <ArrowLeftRight className="w-4 h-4 text-violet-500 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-foreground">Synkronisering</h2>
            <p className="text-[12px] text-muted-foreground truncate">Nyhedsbrev-samtykke · Gecko ↔ HeyLoyalty</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-2 rounded-lg hover:bg-secondary transition-colors shrink-0"
          title="Opdatér statistik"
          data-testid="synk-refresh"
        >
          <RefreshCw className={`w-4 h-4 text-muted-foreground ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!harData ? (
        <p className="text-[13px] text-muted-foreground py-2">
          Ingen synkroniseringer registreret endnu. Connectoren er klar og venter på webhooks fra Gecko og HeyLoyalty.
        </p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatKort label="Synkroniseringer udført" value={g("sync:applied")} accent="text-emerald-600 dark:text-emerald-400" />
          <StatKort label="Tilmeldinger" value={tilmeldt} />
          <StatKort label="Afmeldinger" value={afmeldt} />
          <StatKort label="Events modtaget" value={g("events:received")} />
          <StatKort label="Ekko/dubletter droppet" value={g("events:echo_dropped") + g("events:duplicate")} />
        </div>
      )}
    </div>
  );
}

// System-navne + retning til log-visning.
const SYS_NAVN = { gecko: "Gecko", heyloyalty: "HeyLoyalty", gecko_booking: "Gecko" };

// Oversæt en action/reason til en læsbar dansk label + farve.
function actionLabel(action, reason) {
  if (action === "subscribed")      return { txt: "Tilmeldt",        cls: "text-emerald-600 dark:text-emerald-400" };
  if (action === "unsubscribed")    return { txt: "Afmeldt",         cls: "text-rose-600 dark:text-rose-400" };
  if (action === "profile_updated") return { txt: "Profil opdateret", cls: "text-emerald-600 dark:text-emerald-400" };
  if (action === "noop")            return { txt: "Ingen ændring",   cls: "text-muted-foreground" };
  if (action === "skip") {
    const r = {
      echo: "Ekko (loop-værn)",
      duplicate_event: "Dublet",
      no_identity: "Mangler email",
      no_gecko_customer: "Ukendt i Gecko",
      no_email_for_hl: "Mangler email",
      no_email_for_gecko: "Mangler email",
    }[reason] || "Sprunget over";
    return { txt: r, cls: "text-muted-foreground" };
  }
  if (action === "ignore") return { txt: "Ignoreret", cls: "text-muted-foreground" };
  return { txt: action || "—", cls: "text-foreground" };
}

function fmtTid(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("da-DK", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return ts; }
}

// Fuld-bredde log over de seneste overførsler (kilde → mål, hvem, resultat).
function SynkLog({ recent }) {
  const rows = Array.isArray(recent) ? recent : [];
  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid="synk-log">
      <div className="flex items-center gap-2.5 mb-4 min-w-0">
        <ListChecks className="w-4 h-4 text-violet-500 shrink-0" />
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold text-foreground">Seneste overførsler</h2>
          <p className="text-[12px] text-muted-foreground">De nyeste samtykke-synkroniseringer mellem systemerne</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-[13px] text-muted-foreground py-2">
          Ingen overførsler endnu. Linjer vises her i takt med at webhooks kommer ind.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="font-medium py-2 px-3">Tidspunkt</th>
                <th className="font-medium py-2 px-3">Retning</th>
                <th className="font-medium py-2 px-3">Kontakt</th>
                <th className="font-medium py-2 px-3">Resultat</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e, i) => {
                const lbl = actionLabel(e.action, e.reason);
                const fra = SYS_NAVN[e.src] || e.src || "?";
                const erBooking = e.src === "gecko_booking";
                const til = erBooking ? "HeyLoyalty" : (SYS_NAVN[e.target] || (e.target ? e.target : "—"));
                const overfoert = e.action === "subscribed" || e.action === "unsubscribed" || e.action === "profile_updated";
                return (
                  <tr key={i} className="border-t border-border/60">
                    <td className="py-2 px-3 text-muted-foreground tabular-nums whitespace-nowrap">{fmtTid(e.ts)}</td>
                    <td className="py-2 px-3 whitespace-nowrap">
                      <span className="font-medium text-foreground">{fra}</span>
                      <span className="mx-1.5 text-muted-foreground">{overfoert ? "→" : "·"}</span>
                      <span className={overfoert ? "font-medium text-foreground" : "text-muted-foreground"}>{til}</span>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground font-mono text-[12px]">{e.who || "—"}</td>
                    <td className={`py-2 px-3 font-medium ${lbl.cls}`}>{lbl.txt}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Mapping-side ────────────────────────────────────────────────────────────

const MAPPING_FELTER = [
  // ── Faste HL-felter ───────────────────────────────────────────────────────
  {
    hl_felt: "firstname",
    hl_label: "Fornavn",
    type: "fast",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERFIRSTNAME (eller første ord af CUSTOMERNAME)",
    eksempel: "Sofie",
    note: "Sendes ved tilmelding og booking",
  },
  {
    hl_felt: "lastname",
    hl_label: "Efternavn",
    type: "fast",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERLASTNAME (eller resten af CUSTOMERNAME)",
    eksempel: "Andersen",
    note: "Sendes ved tilmelding og booking",
  },
  {
    hl_felt: "email",
    hl_label: "E-mail",
    type: "fast",
    retning: "Gecko ↔ HL",
    gecko_kilde: "CUSTOMEREMAIL",
    eksempel: "sofie@example.dk",
    note: "Primær nøgle til at matche memberet",
  },
  {
    hl_felt: "mobile",
    hl_label: "Mobilnummer",
    type: "fast",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERMOBILE (kun hvis CUSTOMERACCEPTSMARKETINGSMS = true)",
    eksempel: "51234567",
    note: "Fjernes i HL hvis SMS-samtykke trækkes tilbage",
  },
  {
    hl_felt: "birthdate",
    hl_label: "Fødselsdato",
    type: "fast",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERBIRTHDAY (DD/MM-YYYY) eller BirthYear+Month+Day",
    eksempel: "1990-04-23",
    note: "Konverteres til YYYY-MM-DD for HL",
  },
  {
    hl_felt: "postalcode",
    hl_label: "Postnummer",
    type: "fast",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERPOSTALCODE",
    eksempel: "2100",
    note: "Sendes med booking-webhook",
  },
  {
    hl_felt: "reference",
    hl_label: "Reference",
    type: "fast",
    retning: "—",
    gecko_kilde: "Ikke mappet",
    eksempel: "",
    note: "HL intern brug — sættes ikke fra Gecko",
  },
  // ── Custom HL-felter ──────────────────────────────────────────────────────
  {
    hl_felt: "turnover",
    hl_label: "Turnover",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "ORDERSTOTALVALUE (inkl. moms)",
    eksempel: "4850",
    note: "Samlet omsætning på kunden i kr.",
  },
  {
    hl_felt: "online_booking",
    hl_label: "Online booking",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "ORDERSTOTALCOUNT",
    eksempel: "12",
    note: "Antal bookinger kunden har haft i alt",
  },
  {
    hl_felt: "sidste_booking",
    hl_label: "Sidste booking",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "ORDERSLASTDATE eller CUSTOMERLASTBOOKINGDATE",
    eksempel: "2026-06-15",
    note: "Dato for seneste besøg (YYYY-MM-DD)",
  },
  {
    hl_felt: "last_turnover_date",
    hl_label: "Last turnover date",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "ORDERSLASTDATE eller CUSTOMERLASTBOOKINGDATE",
    eksempel: "2026-06-15",
    note: "Alias for 'Sidste booking' — samme dato",
  },
  {
    hl_felt: "sidste_booking_ydelse",
    hl_label: "Sidste booking ydelse",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERLASTBOOKINGSERVICE",
    eksempel: "Ansigtsbehandling 60 min",
    note: "Navn på den behandling kunden sidst fik",
  },
  {
    hl_felt: "sidste_booking_tekst",
    hl_label: "Sidste booking tekst",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERLASTBOOKINGSERVICE (samme som ydelse)",
    eksempel: "Ansigtsbehandling 60 min",
    note: "Fri tekstgengivelse af seneste behandling",
  },
  {
    hl_felt: "kommende_booking",
    hl_label: "Kommende booking",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERNEXTBOOKINGDATE",
    eksempel: "2026-07-10",
    note: "Dato for næste planlagte besøg (YYYY-MM-DD)",
  },
  {
    hl_felt: "kommende_booking_ydelse",
    hl_label: "Kommende booking ydelse",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERNEXTBOOKINGSERVICE",
    eksempel: "Voks hel ben",
    note: "Behandlingstype ved næste booking",
  },
  {
    hl_felt: "kommende_booking_tekst",
    hl_label: "Kommende booking tekst",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERNEXTBOOKINGSERVICE (samme som ydelse)",
    eksempel: "Voks hel ben",
    note: "Fri tekstgengivelse af næste behandling",
  },
  {
    hl_felt: "kundegruppe_gecko",
    hl_label: "KUNDEGRUPPE GECKO",
    type: "custom",
    retning: "Gecko → HL",
    gecko_kilde: "CUSTOMERTAGS → CUSTOMERTAGNAME (alle tags kommasepareret)",
    eksempel: "VIP, Nyhedsbrev, Stamkunde",
    note: "Gecko-tags samles til én kommasepareret tekststreng",
  },
  {
    hl_felt: "date_created",
    hl_label: "Date Created",
    type: "custom",
    retning: "—",
    gecko_kilde: "Ikke i booking-webhook",
    eksempel: "2024-11-02",
    note: "Sættes ikke automatisk — kan tilføjes via Gecko API-opslag",
  },
  {
    hl_felt: "aabnet",
    hl_label: "Åbnet",
    type: "custom",
    retning: "—",
    gecko_kilde: "Ikke relevant",
    eksempel: "7",
    note: "Styres af HeyLoyalty internt (åbnet-rate for e-mails)",
  },
  {
    hl_felt: "intern",
    hl_label: "Intern",
    type: "custom",
    retning: "—",
    gecko_kilde: "Ikke mappet",
    eksempel: "true",
    note: "Boolean — sættes manuelt i HL for interne kontakter",
  },
];

const RETNING_STIL = {
  "Gecko → HL":  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "Gecko ↔ HL":  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "—":           "bg-secondary text-muted-foreground",
};

function MappingSubside({ onBack }) {
  const aktive  = MAPPING_FELTER.filter(f => f.retning !== "—");
  const inaktive = MAPPING_FELTER.filter(f => f.retning === "—");

  function Tabel({ rækker, titel }) {
    return (
      <div className="mb-8">
        <h3 className="text-[13px] font-semibold text-muted-foreground uppercase tracking-wide mb-3">{titel}</h3>
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="py-2.5 px-4 font-medium">HL felt</th>
                  <th className="py-2.5 px-4 font-medium">Retning</th>
                  <th className="py-2.5 px-4 font-medium">Gecko-kilde (webhook-felt)</th>
                  <th className="py-2.5 px-4 font-medium">Eksempel</th>
                  <th className="py-2.5 px-4 font-medium hidden lg:table-cell">Note</th>
                </tr>
              </thead>
              <tbody>
                {rækker.map((f, i) => (
                  <tr key={f.hl_felt} className={`border-t border-border/60 ${i % 2 === 0 ? "" : "bg-secondary/20"}`}>
                    <td className="py-2.5 px-4 align-top">
                      <div className="font-mono text-[12px] text-foreground">{f.hl_felt}</div>
                      <div className="text-[11px] text-muted-foreground">{f.hl_label}</div>
                    </td>
                    <td className="py-2.5 px-4 align-top whitespace-nowrap">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${RETNING_STIL[f.retning] || RETNING_STIL["—"]}`}>
                        {f.retning}
                      </span>
                    </td>
                    <td className="py-2.5 px-4 align-top">
                      <span className="font-mono text-[11px] text-muted-foreground break-all">{f.gecko_kilde}</span>
                    </td>
                    <td className="py-2.5 px-4 align-top">
                      {f.eksempel ? (
                        <span className="inline-block rounded bg-secondary px-1.5 py-0.5 font-mono text-[12px] text-foreground whitespace-nowrap">
                          {f.eksempel}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 align-top text-[12px] text-muted-foreground hidden lg:table-cell">{f.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ChevronLeft className="w-4 h-4" /> Tilbage til Lister
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Felt-mapping</h1>
        <p className="text-[13px] text-muted-foreground mt-1">
          Gecko booking-webhook → HeyLoyalty liste 29891 (Beauty &amp; Style).
          Alle felter opdateres automatisk via <span className="font-mono text-[12px]">POST /consent-sync/webhooks/gecko-booking</span>.
        </p>
      </div>

      <Tabel rækker={aktive} titel="Aktive mappings — synkes automatisk" />
      <Tabel rækker={inaktive} titel="Ikke mappet — sættes manuelt i HL" />

      <div className="rounded-2xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4 text-[13px] text-amber-800 dark:text-amber-300">
        <strong>Triggerlogik:</strong> Gecko skal konfigureres til at kalde{" "}
        <span className="font-mono text-[12px]">POST /api/consent-sync/webhooks/gecko-booking</span> ved oprettelse og ændring af en booking.
        Samtykke (tilmeld/afmeld) synkes separat via <span className="font-mono text-[12px]">/webhooks/gecko</span>.
      </div>
    </div>
  );
}

// Kort der viser én liste med antal medlemmer.
function ListeKort({ liste }) {
  const icon = LISTE_ICON[liste.id] || <Users className="w-6 h-6" />;
  return (
    <div
      className="rounded-2xl border border-border bg-card p-5 flex items-center gap-4"
      data-testid={`liste-kort-${liste.id}`}
    >
      <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center text-muted-foreground shrink-0">
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-[15px] font-semibold text-foreground truncate">{liste.navn}</h2>
        {liste.koblet ? (
          <p className="text-[12px] text-muted-foreground">Medlemmer</p>
        ) : (
          <p className="text-[12px] text-amber-600 dark:text-amber-400">Ikke koblet endnu</p>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-2xl font-bold tabular-nums text-foreground">
          {liste.koblet ? liste.antal.toLocaleString("da-DK") : "—"}
        </div>
      </div>
    </div>
  );
}

export default function ListerPage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const isSuperAdmin = user && ["superadmin", "owner", "api_admin"].includes(user.role);
  const handleLogout = () => { logout(); navigate("/login"); };

  const [visMappingside, setVisMappingside] = useState(false);

  const [lister, setLister] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [synkStats, setSynkStats] = useState(null);
  const [synkLoading, setSynkLoading] = useState(false);

  const hentOversigt = useCallback(async (force = false) => {
    if (!isSuperAdmin) return;
    setLoading(true);
    setError(null);
    try {
      // force=true tvinger backend til at hente friske HeyLoyalty-tal (ellers ~10 min cache).
      const res = await axios.get(`${API}/admin/lister/oversigt${force ? "?refresh=1" : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLister(res.data?.lister || []);
    } catch (e) {
      setError("Kunne ikke hente oversigten. Prøv igen.");
    } finally {
      setLoading(false);
    }
  }, [token, isSuperAdmin]);

  const hentSynkStats = useCallback(async () => {
    if (!isSuperAdmin) return;
    setSynkLoading(true);
    try {
      const res = await axios.get(`${API}/consent-sync/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSynkStats(res.data || {});
    } catch (e) {
      // Stille fejl — statusboksen viser bare "ingen data" hvis den ikke kan hentes.
      setSynkStats({});
    } finally {
      setSynkLoading(false);
    }
  }, [token, isSuperAdmin]);

  useEffect(() => { hentOversigt(); hentSynkStats(); }, [hentOversigt, hentSynkStats]);

  if (!user) return null;
  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <DashboardHeader user={user} onLogout={handleLogout} />
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <AccessDenied role="superadmin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <DashboardHeader user={user} onLogout={handleLogout} />

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {visMappingside ? (
          <MappingSubside onBack={() => setVisMappingside(false)} />
        ) : (
          <>
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Lister</h1>
                <p className="text-[13px] text-muted-foreground">Oversigt over antal medlemmer i de tre kategorier</p>
              </div>
              <button
                onClick={() => setVisMappingside(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-border bg-card hover:bg-secondary transition-colors text-[13px] font-medium text-muted-foreground whitespace-nowrap shrink-0"
              >
                <Map className="w-4 h-4" /> Felt-mapping
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Henter…
              </div>
            ) : error ? (
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <p className="text-sm text-red-500 mb-3">{error}</p>
                <button
                  onClick={hentOversigt}
                  className="px-4 py-2 rounded-xl border border-border bg-secondary hover:bg-secondary/80 text-sm font-medium transition-colors"
                >
                  Prøv igen
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lister.map((liste) => (
                    <ListeKort key={liste.id} liste={liste} />
                  ))}
                </div>
                <SynkStatus stats={synkStats} loading={synkLoading} onRefresh={() => { hentSynkStats(); hentOversigt(true); }} />
                <SynkLog recent={synkStats?._recent} />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
